import React, { useState } from "react";
import { PageLayout } from "../ui-components/PageLayout";
import {
  Select,
  MenuItem,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  FinancialLineChartWrapper,
  hasPipelineDots,
} from "../ui-components/Charts/FinancialLineChartWrapper";
import { RootState } from "../store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { getFinancialCategoryDataSummary } from "../store/modules/analytics/analyticsActions";
import { AppDispatch } from "../store/store";
import LoadingDialog from "../ui-components/Dialogs/LoadingDialog";
import {
  NO_YEARS_SELECTED,
  YearFilterSelect,
} from "../ui-components/YearFilterSelect";
import { TransactionTypeFilterSelect } from "../ui-components/TransactionTypeFilterSelect";
import { usePipelineFolder } from "../hooks/usePipeline";
import { useQuery } from "@tanstack/react-query";
import { FinancialCategoryEnumModel } from "../types/types";
import { CoAgentFilterSelect } from "../ui-components/CoAgentFilterSelect";
import { usePageCoAgentFilter } from "../hooks/usePageCoAgentFilter";

const financialCategories = [
  FinancialCategoryEnumModel.Commission,
  FinancialCategoryEnumModel.Gci,
  FinancialCategoryEnumModel.EffectiveGci,
  FinancialCategoryEnumModel.BrokerageTotal,
  FinancialCategoryEnumModel.NetCommissionIncome,
  FinancialCategoryEnumModel.NetIncome,
  FinancialCategoryEnumModel.NetIncomePercent,
] as const;

const financialCategoryLabels = {
  [FinancialCategoryEnumModel.Commission]: "Average Commission Percentage",
  [FinancialCategoryEnumModel.Gci]: "GCI",
  [FinancialCategoryEnumModel.EffectiveGci]: "Effective GCI",
  [FinancialCategoryEnumModel.BrokerageTotal]: "Brokerage Total Fees",
  [FinancialCategoryEnumModel.NetCommissionIncome]: "Net Commission Income",
  [FinancialCategoryEnumModel.NetIncome]: "Net Income",
  [FinancialCategoryEnumModel.NetIncomePercent]: "Average Income Percentage",
} as const satisfies Record<FinancialCategoryEnumModel, string>;

const financialCategoryDescriptions = {
  [FinancialCategoryEnumModel.Commission]:
    "This average excludes rentals and referrals out.",
  [FinancialCategoryEnumModel.Gci]: undefined,
  [FinancialCategoryEnumModel.EffectiveGci]:
    " GCI received by brokerage after client rebates and referral fees, if any.",
  [FinancialCategoryEnumModel.BrokerageTotal]: undefined,
  [FinancialCategoryEnumModel.NetCommissionIncome]:
    "Commission income received by agent after paying brokerage fees",
  [FinancialCategoryEnumModel.NetIncome]:
    "Net Income represents your Net Commission Income minus any out-of-pocket expenses.",
  [FinancialCategoryEnumModel.NetIncomePercent]:
    "Percentage of net income compared to sale price. This average excludes rentals and referrals out.",
} as const satisfies Record<FinancialCategoryEnumModel, string | undefined>;
const FinancialTrends = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [selectedCategory, setSelectedCategory] =
    useState<FinancialCategoryEnumModel>(FinancialCategoryEnumModel.Commission);
  const { years } = useSelector((state: RootState) => state.transactionYear);
  const transactionTypes = useSelector(
    (state: RootState) => state.commonData.userData.data?.transactionTypes,
  );
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  const [selectedTransactionTypeIds, setSelectedTransactionTypeIds] = useState<
    number[]
  >([]);
  const [timePeriod, setTimePeriod] = useState<"quarter" | "month" | "year">(
    "year",
  );

  const { data: pipelineFolder } = usePipelineFolder();
  const pipelineYearId = pipelineFolder?.id;
  // "Under Contracts" adds a dot per period; it is a flag, not a year.
  const [underContractsChecked, setIncludeUnderContracts] = useState(false);

  // Only the commission % and GCI can show Active and Under Contract dots: the other tabs hide
  // those options and leave them out of the request (kept for when the user switches back)
  const showsDots = hasPipelineDots(selectedCategory);
  const includeUnderContracts = underContractsChecked && showsDots;
  const showActive = showsDots && pipelineYearId !== undefined;
  const requestYearIds = (() => {
    if (showsDots || pipelineYearId === undefined) return selectedYearIds;
    const ids = selectedYearIds.filter((id) => id !== pipelineYearId);
    // Only "Active" selected must match nothing, not fall back to "all years" ([])
    return selectedYearIds.length > 0 && ids.length === 0
      ? [NO_YEARS_SELECTED]
      : ids;
  })();
  // Real year folders picked (Active is shown as dots, never as a year)
  const realSelectedYearCount = selectedYearIds.filter(
    (id) => id !== pipelineYearId && id !== NO_YEARS_SELECTED,
  ).length;

  const handleYearsChange = (value: number[]) => {
    // The other tabs don't list Active: keep it selected for when the user switches back,
    // unless "Check all" ([]) or "Uncheck all" was picked
    const keepActive =
      !showsDots &&
      pipelineYearId !== undefined &&
      selectedYearIds.includes(pipelineYearId) &&
      value.length > 0 &&
      !value.includes(NO_YEARS_SELECTED);
    setSelectedYearIds(keepActive ? [...value, pipelineYearId] : value);
    // A single year is shown by quarter
    if (
      value.filter((id) => id !== pipelineYearId && id !== NO_YEARS_SELECTED)
        .length === 1
    ) {
      setTimePeriod("quarter");
    }
  };
  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const coAgents = userData?.coAgents || [];

  // Page-specific co-agent filter
  const [effectiveCoAgentIds, setPageCoAgentIds, isGlobalFilterActive] =
    usePageCoAgentFilter("financialTrends", selectedCoAgentIds);

  const [touchStart, setTouchStart] = useState(0);
  const handleTouchStart = (e: React.TouchEvent) =>
    setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      const currentIndex = financialCategories.indexOf(selectedCategory);
      const newIndex = diff > 0 ? currentIndex + 1 : currentIndex - 1;
      setSelectedCategory(
        financialCategories[
          Math.max(0, Math.min(newIndex, financialCategories.length - 1))
        ],
      );
    }
  };

  const { financialCategoryState } = useSelector(
    (state: RootState) => state.analytics,
  );
  const loading = financialCategoryState.loading;

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: FinancialCategoryEnumModel,
  ) => {
    setSelectedCategory(newValue);
  };

  useQuery({
    queryKey: [
      "getFinancialCategoryDataSummary",
      selectedCategory,
      requestYearIds,
      effectiveCoAgentIds,
      selectedTransactionTypeIds,
      includeUnderContracts,
    ],
    queryFn: () => {
      // The API adds the Under Contracts of every year folder, whatever years are selected
      dispatch(
        getFinancialCategoryDataSummary(
          requestYearIds,
          selectedCategory,
          effectiveCoAgentIds,
          selectedTransactionTypeIds,
          includeUnderContracts,
        ),
      );
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
    enabled: selectedCategory !== undefined,
  });

  return (
    <PageLayout>
      <LoadingDialog open={loading} message="Loading Financial Data..." />
      <Box sx={{ mb: 6, ml: 1 }}>
        <Typography
          variant="h4"
          color="primary"
          component="h1"
          fontWeight="bold"
          gutterBottom
        >
          Financial Trends
        </Typography>

        {/* Filter controls, as in Analytics (no Units/Volume/Average Price) */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            mb: 4,
            mt: 2,
          }}
        >
          <YearFilterSelect
            years={years}
            selectedYearIds={requestYearIds}
            onChange={handleYearsChange}
            label={showActive ? "Years/Pipeline" : "Years"}
            pipelineYearId={showActive ? pipelineYearId : undefined}
            pipelineLabel="Active"
            underContractsChecked={underContractsChecked}
            onUnderContractsChange={
              showsDots ? setIncludeUnderContracts : undefined
            }
            bulkActions
            size="small"
            fullWidth={false}
            sx={{
              minWidth: 150,
              flex: "0 1 200px",
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />

          <TransactionTypeFilterSelect
            transactionTypes={transactionTypes || []}
            selectedTransactionTypeIds={selectedTransactionTypeIds}
            onChange={setSelectedTransactionTypeIds}
            label="Transaction Type"
            size="small"
            fullWidth={false}
            sx={{
              minWidth: 160,
              flex: "0 1 210px",
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />

          {!isGlobalFilterActive &&
            !hiddenFields?.some((field) => field.name === "coAgent") && (
              <CoAgentFilterSelect
                coAgents={coAgents}
                selectedCoAgentIds={effectiveCoAgentIds}
                onChange={setPageCoAgentIds}
                label="Filter by Team Members"
                size="small"
                fullWidth={false}
                sx={{
                  minWidth: 170,
                  flex: "0 1 220px",
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            )}

          <Tooltip title="View mode">
            <ToggleButtonGroup
              value={timePeriod}
              exclusive
              onChange={(_, newTimePeriod) =>
                newTimePeriod && setTimePeriod(newTimePeriod)
              }
              size="small"
              aria-label="View mode"
              sx={{
                "& .MuiToggleButton-root": {
                  px: 1.5,
                  py: 0.5,
                  whiteSpace: "nowrap",
                  borderColor: "#dee2e6",
                  color: "#6c757d",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                },
              }}
            >
              {realSelectedYearCount !== 1 && (
                <ToggleButton value="year">Yearly</ToggleButton>
              )}
              <ToggleButton value="quarter">Quarterly</ToggleButton>
              <ToggleButton value="month">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        </Box>

        {useMediaQuery(useTheme().breakpoints.up("sm")) ? (
          <Box
            sx={{
              background: "#F7F9FB",
              borderRadius: "8px",
              px: 2,
              py: 1,
              mb: 3,
              display: "flex",
              alignItems: "center",
              minHeight: 56,
            }}
          >
            <Tabs
              value={selectedCategory}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              sx={{
                minHeight: 40,
                "& .MuiTabs-flexContainer": { gap: 0 },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "1rem",
                  minHeight: 40,
                  minWidth: 120,
                  borderRadius: "8px 8px 0 0",
                  color: "#222",
                  background: "transparent",
                  "&.Mui-selected": {
                    background: "#fff",
                    color: "primary.dark",
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                  },
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {financialCategories.map((category) => (
                <Tab
                  label={financialCategoryLabels[category]}
                  value={category}
                  key={category}
                />
              ))}
            </Tabs>
          </Box>
        ) : (
          <Select
            value={selectedCategory}
            onChange={(e) =>
              setSelectedCategory(e.target.value as FinancialCategoryEnumModel)
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            {financialCategories.map((category) => (
              <MenuItem value={category} key={category}>
                {financialCategoryLabels[category]}
              </MenuItem>
            ))}
          </Select>
        )}

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography variant="h6" component="div">
                      {financialCategoryLabels[selectedCategory]} Trend Analysis
                    </Typography>
                    {financialCategoryDescriptions[selectedCategory] && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontStyle: "italic" }}
                      >
                        {financialCategoryDescriptions[selectedCategory]}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <FinancialLineChartWrapper
                    category={selectedCategory}
                    selectedYearIds={requestYearIds}
                    timePeriod={timePeriod}
                    includeUnderContracts={includeUnderContracts}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default FinancialTrends;
